import React, { useState, useEffect, useMemo } from "react";
import {
  BedDouble,
  Calendar,
  CalendarCheck,
  User,
  Users,
  Phone,
  Clock,
  CreditCard,
  Plus,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  X,
  Search,
  Filter,
  Sparkles,
  Layers,
  DoorClosed,
  AlertCircle,
  HelpCircle,
  IndianRupee,
  Check,
  ShieldCheck,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { getSupabaseClient } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { GuestKYCSection } from "./GuestKYCSection";
import {
  uploadGuestKYCDocument,
  CustomerKYCProfile,
} from "../../lib/kycUtils";

export interface RoomTypeInfo {
  id: string;
  type_name: string;
  base_price: number;
  max_occupancy: number;
}

export interface HotelRoomItem {
  id: string;
  room_number: string;
  floor: number | string | null;
  housekeeping_status: string;
  is_active?: boolean;
  room_type_id?: string | null;
  room_types?: RoomTypeInfo | null;
}

export interface ActiveRoomBooking {
  id: string;
  room_id: string;
  status: "booked" | "checked_in" | "checked_out" | "cancelled";
  guest_name: string;
  guest_phone?: string | null;
  check_in_date: string;
  check_out_date: string;
  num_guests?: number;
  total_room_charge?: number;
  final_bill_amount?: number;
  created_at?: string;
}

export interface HotelRoomGridProps {
  branchId?: string | null;
  onClose?: () => void;
  compact?: boolean;
}

export const HotelRoomGrid: React.FC<HotelRoomGridProps> = ({
  branchId: propBranchId,
  onClose,
  compact = false,
}) => {
  const { currentTenantId, activeBranchId, user } = useAuth();
  const effectiveBranchId = propBranchId || activeBranchId || null;

  // Data states
  const [rooms, setRooms] = useState<HotelRoomItem[]>([]);
  const [activeBookings, setActiveBookings] = useState<ActiveRoomBooking[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomTypeInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "available" | "occupied" | "dirty" | "out_of_order" | "reserved"
  >("all");
  const [floorFilter, setFloorFilter] = useState<string>("all");

  // New Booking Modal States
  const [showBookingModal, setShowBookingModal] = useState<boolean>(false);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState<string>("");
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");
  const [guestName, setGuestName] = useState<string>("");
  const [guestPhone, setGuestPhone] = useState<string>("");

  // Guest KYC & Booking Type States
  const [bookingStatus, setBookingStatus] = useState<"booked" | "checked_in">("booked");
  const [guestAddress, setGuestAddress] = useState<string>("");
  const [idProofType, setIdProofType] = useState<string>("");
  const [idProofNumber, setIdProofNumber] = useState<string>("");
  const [purposeOfStay, setPurposeOfStay] = useState<string>("");
  const [idDocFile, setIdDocFile] = useState<File | null>(null);
  const [existingDocUrl, setExistingDocUrl] = useState<string | null>(null);
  const [matchedCustomer, setMatchedCustomer] = useState<CustomerKYCProfile | null>(null);

  // Default dates: check-in = today, check-out = tomorrow
  const getTodayDateStr = () => {
    const d = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const getTomorrowDateStr = (baseDateStr?: string) => {
    const d = baseDateStr ? new Date(baseDateStr) : new Date();
    d.setDate(d.getDate() + 1);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const [checkInDate, setCheckInDate] = useState<string>(getTodayDateStr());
  const [checkOutDate, setCheckOutDate] = useState<string>(getTomorrowDateStr());
  const [numGuests, setNumGuests] = useState<number>(1);
  const [paymentMethod, setPaymentMethod] = useState<string>("pay_later");
  const [submittingBooking, setSubmittingBooking] = useState<boolean>(false);
  const [bookingErrorMessage, setBookingErrorMessage] = useState<string | null>(null);

  // Fetch Rooms, Bookings, and Room Types
  const fetchHotelData = async (isRefresh: boolean = false) => {
    if (!effectiveBranchId && !currentTenantId) {
      setLoading(false);
      return;
    }

    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      // 1. Fetch Rooms for branch
      let roomsQuery = supabase
        .from("rooms")
        .select(
          "id, room_number, floor, housekeeping_status, is_active, room_type_id, room_types(id, type_name, base_price, max_occupancy)"
        )
        .eq("is_active", true)
        .order("room_number");

      if (effectiveBranchId) {
        roomsQuery = roomsQuery.eq("branch_id", effectiveBranchId);
      } else if (currentTenantId) {
        roomsQuery = roomsQuery.eq("tenant_id", currentTenantId);
      }

      const { data: roomsData, error: roomsErr } = await roomsQuery;
      if (roomsErr) {
        console.error("Error fetching rooms:", roomsErr);
      }

      // 2. Fetch Active Bookings (both checked_in and booked)
      let bookingsQuery = supabase
        .from("room_bookings")
        .select(
          "id, room_id, status, guest_name, guest_phone, check_in_date, check_out_date, num_guests, total_room_charge, final_bill_amount, created_at"
        )
        .in("status", ["checked_in", "booked"]);

      if (effectiveBranchId) {
        bookingsQuery = bookingsQuery.eq("branch_id", effectiveBranchId);
      } else if (currentTenantId) {
        bookingsQuery = bookingsQuery.eq("tenant_id", currentTenantId);
      }

      const { data: bookingsData, error: bookingsErr } = await bookingsQuery;
      if (bookingsErr) {
        console.error("Error fetching bookings:", bookingsErr);
      }

      // 3. Fetch Room Types for the dropdown
      let typesQuery = supabase
        .from("room_types")
        .select("id, type_name, base_price, max_occupancy")
        .order("type_name");

      if (currentTenantId) {
        typesQuery = typesQuery.eq("tenant_id", currentTenantId);
      }

      const { data: typesData, error: typesErr } = await typesQuery;
      if (typesErr) {
        console.error("Error fetching room types:", typesErr);
      }

      setRooms((roomsData as HotelRoomItem[]) || []);
      setActiveBookings((bookingsData as ActiveRoomBooking[]) || []);
      setRoomTypes((typesData as RoomTypeInfo[]) || []);
    } catch (err: any) {
      console.error("fetchHotelData error:", err);
      toast.error("Failed to load hotel room data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchHotelData();
  }, [effectiveBranchId, currentTenantId]);

  // Derived Bookings Maps
  const occupiedBookingsMap = useMemo(() => {
    const map = new Map<string, ActiveRoomBooking>();
    activeBookings.forEach((b) => {
      if (b.status === "checked_in") {
        map.set(b.room_id, b);
      }
    });
    return map;
  }, [activeBookings]);

  const reservedBookingsMap = useMemo(() => {
    const map = new Map<string, ActiveRoomBooking>();
    activeBookings.forEach((b) => {
      if (b.status === "booked") {
        map.set(b.room_id, b);
      }
    });
    return map;
  }, [activeBookings]);

  // Determine Room Status & Details
  const getRoomComputedStatus = (
    room: HotelRoomItem
  ): {
    status: "occupied" | "dirty" | "out_of_order" | "reserved" | "available";
    label: string;
    badgeBg: string;
    badgeText: string;
    borderClass: string;
    cardBg: string;
    booking?: ActiveRoomBooking;
  } => {
    const occupiedBooking = occupiedBookingsMap.get(room.id);
    if (occupiedBooking) {
      return {
        status: "occupied",
        label: "Occupied",
        badgeBg: "bg-red-500",
        badgeText: "text-red-700 dark:text-red-300",
        borderClass: "border-red-300 dark:border-red-800/80 shadow-red-500/5",
        cardBg: "bg-red-50/60 dark:bg-red-950/25",
        booking: occupiedBooking,
      };
    }

    if (room.housekeeping_status === "dirty") {
      return {
        status: "dirty",
        label: "Needs Cleaning",
        badgeBg: "bg-amber-500",
        badgeText: "text-amber-700 dark:text-amber-300",
        borderClass: "border-amber-300 dark:border-amber-800/80 shadow-amber-500/5",
        cardBg: "bg-amber-50/60 dark:bg-amber-950/25",
      };
    }

    if (room.housekeeping_status === "out_of_order") {
      return {
        status: "out_of_order",
        label: "Out of Order",
        badgeBg: "bg-slate-400 dark:bg-slate-600",
        badgeText: "text-slate-600 dark:text-slate-400",
        borderClass: "border-slate-300 dark:border-slate-800 shadow-none",
        cardBg: "bg-slate-100/70 dark:bg-slate-900/60",
      };
    }

    const reservedBooking = reservedBookingsMap.get(room.id);
    if (reservedBooking) {
      return {
        status: "reserved",
        label: "Reserved",
        badgeBg: "bg-blue-500",
        badgeText: "text-blue-700 dark:text-blue-300",
        borderClass: "border-blue-300 dark:border-blue-800 shadow-blue-500/5",
        cardBg: "bg-blue-50/50 dark:bg-blue-950/20",
        booking: reservedBooking,
      };
    }

    return {
      status: "available",
      label: "Available",
      badgeBg: "bg-emerald-500",
      badgeText: "text-emerald-700 dark:text-emerald-300",
      borderClass: "border-emerald-300 dark:border-emerald-800/80 shadow-emerald-500/5",
      cardBg: "bg-emerald-50/50 dark:bg-emerald-950/20",
    };
  };

  // Stats calculation
  const stats = useMemo(() => {
    let total = rooms.length;
    let available = 0;
    let occupied = 0;
    let dirty = 0;
    let outOfOrder = 0;
    let reserved = 0;

    rooms.forEach((r) => {
      const computed = getRoomComputedStatus(r);
      if (computed.status === "occupied") occupied++;
      else if (computed.status === "dirty") dirty++;
      else if (computed.status === "out_of_order") outOfOrder++;
      else if (computed.status === "reserved") reserved++;
      else available++;
    });

    return { total, available, occupied, dirty, outOfOrder, reserved };
  }, [rooms, occupiedBookingsMap, reservedBookingsMap]);

  // Unique floors for filter
  const uniqueFloors = useMemo(() => {
    const floorsSet = new Set<string>();
    rooms.forEach((r) => {
      if (r.floor !== null && r.floor !== undefined) {
        floorsSet.add(String(r.floor));
      }
    });
    return Array.from(floorsSet).sort((a, b) => Number(a) - Number(b));
  }, [rooms]);

  // Filtered rooms list
  const filteredRooms = useMemo(() => {
    return rooms.filter((room) => {
      // Status filter
      if (statusFilter !== "all") {
        const computed = getRoomComputedStatus(room);
        if (computed.status !== statusFilter) return false;
      }

      // Floor filter
      if (floorFilter !== "all") {
        if (String(room.floor) !== floorFilter) return false;
      }

      // Search query filter (matches room_number, type_name, or guest name)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const numMatch = room.room_number?.toLowerCase().includes(q);
        const typeMatch = room.room_types?.type_name?.toLowerCase().includes(q);
        const booking =
          occupiedBookingsMap.get(room.id) || reservedBookingsMap.get(room.id);
        const guestMatch = booking?.guest_name?.toLowerCase().includes(q);

        if (!numMatch && !typeMatch && !guestMatch) return false;
      }

      return true;
    });
  }, [
    rooms,
    statusFilter,
    floorFilter,
    searchQuery,
    occupiedBookingsMap,
    reservedBookingsMap,
  ]);

  // Filter available rooms for New Booking Modal
  const availableRoomsForSelectedType = useMemo(() => {
    if (!selectedRoomTypeId) return [];

    return rooms.filter((room) => {
      // Must match selected room type
      const matchesType =
        room.room_type_id === selectedRoomTypeId ||
        room.room_types?.id === selectedRoomTypeId;
      if (!matchesType) return false;

      // Must be physically available (not occupied, not dirty, not out of order)
      const isOccupied = occupiedBookingsMap.has(room.id);
      const isDirty = room.housekeeping_status === "dirty";
      const isOutOfOrder = room.housekeeping_status === "out_of_order";

      return !isOccupied && !isDirty && !isOutOfOrder;
    });
  }, [rooms, selectedRoomTypeId, occupiedBookingsMap]);

  // Auto-select room when type changes or initialize
  const handleRoomTypeChange = (typeId: string) => {
    setSelectedRoomTypeId(typeId);
    setBookingErrorMessage(null);

    const typeObj = roomTypes.find((t) => t.id === typeId);
    if (typeObj && typeObj.max_occupancy) {
      if (numGuests > typeObj.max_occupancy) {
        setNumGuests(typeObj.max_occupancy);
      }
    }

    // Filter available rooms for this type
    const avail = rooms.filter((r) => {
      const matchesType =
        r.room_type_id === typeId || r.room_types?.id === typeId;
      const isOccupied = occupiedBookingsMap.has(r.id);
      const isDirty = r.housekeeping_status === "dirty";
      const isOutOfOrder = r.housekeeping_status === "out_of_order";
      return matchesType && !isOccupied && !isDirty && !isOutOfOrder;
    });

    if (avail.length > 0) {
      setSelectedRoomId(avail[0].id);
    } else {
      setSelectedRoomId("");
    }
  };

  // Open modal with preselected room
  const handleOpenBookingModalForRoom = (room: HotelRoomItem) => {
    const typeId = room.room_type_id || room.room_types?.id;
    if (typeId) {
      setSelectedRoomTypeId(typeId);
    } else if (roomTypes.length > 0) {
      setSelectedRoomTypeId(roomTypes[0].id);
    }

    setSelectedRoomId(room.id);
    setCheckInDate(getTodayDateStr());
    setCheckOutDate(getTomorrowDateStr());
    setGuestName("");
    setGuestPhone("");
    setGuestAddress("");
    setIdProofType("");
    setIdProofNumber("");
    setPurposeOfStay("");
    setIdDocFile(null);
    setExistingDocUrl(null);
    setMatchedCustomer(null);
    setBookingStatus("booked");
    setNumGuests(1);
    setPaymentMethod("pay_later");
    setBookingErrorMessage(null);
    setShowBookingModal(true);
  };

  // Open modal from top button
  const handleOpenNewBookingModal = () => {
    setBookingErrorMessage(null);
    setGuestName("");
    setGuestPhone("");
    setGuestAddress("");
    setIdProofType("");
    setIdProofNumber("");
    setPurposeOfStay("");
    setIdDocFile(null);
    setExistingDocUrl(null);
    setMatchedCustomer(null);
    setBookingStatus("booked");
    setCheckInDate(getTodayDateStr());
    setCheckOutDate(getTomorrowDateStr());
    setPaymentMethod("pay_later");
    setNumGuests(1);

    if (roomTypes.length > 0) {
      const defaultTypeId = roomTypes[0].id;
      setSelectedRoomTypeId(defaultTypeId);

      // find first available room of this type
      const avail = rooms.filter((r) => {
        const matchesType =
          r.room_type_id === defaultTypeId ||
          r.room_types?.id === defaultTypeId;
        const isOccupied = occupiedBookingsMap.has(r.id);
        const isDirty = r.housekeeping_status === "dirty";
        const isOutOfOrder = r.housekeeping_status === "out_of_order";
        return matchesType && !isOccupied && !isDirty && !isOutOfOrder;
      });

      if (avail.length > 0) {
        setSelectedRoomId(avail[0].id);
      } else {
        setSelectedRoomId("");
      }
    } else {
      setSelectedRoomTypeId("");
      setSelectedRoomId("");
    }

    setShowBookingModal(true);
  };

  // Calculate pricing breakdown
  const pricingSummary = useMemo(() => {
    const selectedRoom = rooms.find((r) => r.id === selectedRoomId);
    const selectedType = roomTypes.find((t) => t.id === selectedRoomTypeId);
    const basePrice =
      selectedRoom?.room_types?.base_price || selectedType?.base_price || 0;

    let nights = 1;
    try {
      const inD = new Date(checkInDate);
      const outD = new Date(checkOutDate);
      const diffTime = outD.getTime() - inD.getTime();
      nights = Math.max(1, Math.round(diffTime / (1000 * 60 * 60 * 24)));
    } catch {
      nights = 1;
    }

    const roomCharge = basePrice * nights;
    const gstRate = 0.12; // 12% standard hospitality GST in schema
    const gstAmount = Math.round(roomCharge * gstRate * 100) / 100;
    const totalAmount = roomCharge + gstAmount;

    return {
      nights,
      basePrice,
      roomCharge,
      gstAmount,
      totalAmount,
      selectedRoom,
    };
  }, [rooms, roomTypes, selectedRoomId, selectedRoomTypeId, checkInDate, checkOutDate]);

  // Handle Create Booking Submit
  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setBookingErrorMessage(null);

    // Validation
    if (!selectedRoomId) {
      setBookingErrorMessage("Please select an available room.");
      toast.error("Please select a room");
      return;
    }

    if (!guestName.trim()) {
      setBookingErrorMessage("Guest Name is required.");
      toast.error("Please enter guest name");
      return;
    }

    if (!guestPhone.trim()) {
      setBookingErrorMessage("Guest Phone Number is required.");
      toast.error("Please enter guest phone");
      return;
    }

    if (guestPhone.trim().replace(/\D/g, "").length < 10) {
      setBookingErrorMessage("Please enter a valid 10-digit phone number.");
      toast.error("Phone number must be at least 10 digits");
      return;
    }

    // Checked-in / Walk-in Mandatory KYC Check
    if (bookingStatus === "checked_in") {
      if (!idProofNumber.trim()) {
        setBookingErrorMessage("Check-in ke liye ID Proof Number bharna mandatory hai.");
        toast.error("Check-in ke liye ID Proof Number bharna mandatory hai.");
        return;
      }
      if (!existingDocUrl && !idDocFile) {
        setBookingErrorMessage("Check-in ke liye ID Document Scan upload karna mandatory hai.");
        toast.error("Check-in ke liye ID Document Scan upload karna mandatory hai.");
        return;
      }
    }

    if (!checkInDate) {
      setBookingErrorMessage("Check-in date is required.");
      toast.error("Please select check-in date");
      return;
    }

    if (!checkOutDate) {
      setBookingErrorMessage("Check-out date is required.");
      toast.error("Please select check-out date");
      return;
    }

    if (checkOutDate <= checkInDate) {
      setBookingErrorMessage("Check-out date must be after Check-in date.");
      toast.error("Check-out date must be after check-in date");
      return;
    }

    const selectedRoom = rooms.find((r) => r.id === selectedRoomId);
    const roomNumber = selectedRoom?.room_number || "Selected";

    const supabase = getSupabaseClient();
    if (!supabase) {
      setBookingErrorMessage("Database connection not ready. Please try again.");
      return;
    }

    const tenantIdToUse = currentTenantId || selectedRoom?.room_types?.id;
    const branchIdToUse = effectiveBranchId;
    const userIdToUse = user?.id;

    if (!tenantIdToUse || !branchIdToUse) {
      setBookingErrorMessage("Tenant or Branch identifier missing.");
      return;
    }

    setSubmittingBooking(true);

    try {
      // 1. Upload ID document to guest-documents bucket if a new file is selected
      let finalDocUrl = existingDocUrl;
      if (idDocFile && tenantIdToUse) {
        try {
          finalDocUrl = await uploadGuestKYCDocument(tenantIdToUse, null, idDocFile);
        } catch (uploadErr: any) {
          setBookingErrorMessage(uploadErr.message || "Failed to upload ID document.");
          toast.error(uploadErr.message || "ID Document upload failed");
          setSubmittingBooking(false);
          return;
        }
      }

      // 2. Save or update retail_customers master record
      let finalCustomerId: string | null = null;
      if (matchedCustomer?.id) {
        const custUpdate: any = {
          customer_name: guestName.trim(),
          phone: guestPhone.trim(),
          address: guestAddress.trim() || null,
          id_proof_type: idProofType || null,
          id_proof_number: idProofNumber.trim() || null,
        };
        if (finalDocUrl) custUpdate.id_document_url = finalDocUrl;
        await supabase.from("retail_customers").update(custUpdate).eq("id", matchedCustomer.id);
        finalCustomerId = matchedCustomer.id;
      } else {
        const { data: existingCust } = await supabase
          .from("retail_customers")
          .select("id")
          .eq("tenant_id", tenantIdToUse)
          .eq("phone", guestPhone.trim())
          .maybeSingle();

        if (existingCust?.id) {
          const custUpdate: any = {
            customer_name: guestName.trim(),
            address: guestAddress.trim() || null,
            id_proof_type: idProofType || null,
            id_proof_number: idProofNumber.trim() || null,
          };
          if (finalDocUrl) custUpdate.id_document_url = finalDocUrl;
          await supabase.from("retail_customers").update(custUpdate).eq("id", existingCust.id);
          finalCustomerId = existingCust.id;
        } else {
          const custInsert: any = {
            tenant_id: tenantIdToUse,
            customer_name: guestName.trim(),
            phone: guestPhone.trim(),
            address: guestAddress.trim() || null,
            id_proof_type: idProofType || null,
            id_proof_number: idProofNumber.trim() || null,
            customer_group: "Hotel Guest",
          };
          if (finalDocUrl) custInsert.id_document_url = finalDocUrl;
          const { data: newCust, error: insertErr } = await supabase
            .from("retail_customers")
            .insert([custInsert])
            .select("id")
            .single();
          if (!insertErr && newCust) {
            finalCustomerId = newCust.id;
          }
        }
      }

      // 3. Create room booking via RPC
      const { data, error } = await supabase.rpc("create_room_booking", {
        p_tenant_id: tenantIdToUse,
        p_branch_id: branchIdToUse,
        p_room_id: selectedRoomId,
        p_user_id: userIdToUse,
        p_guest_name: guestName.trim(),
        p_guest_phone: guestPhone.trim(),
        p_check_in_date: checkInDate,
        p_check_out_date: checkOutDate,
        p_num_guests: Number(numGuests) || 1,
        p_payment_method: paymentMethod || "pay_later",
      });

      if (error) {
        console.error("create_room_booking error:", error);
        if (
          error.message &&
          error.message.includes("Room is already booked for the selected dates")
        ) {
          setBookingErrorMessage(
            "Room is already booked for the selected dates. Kripya doosra room chunein ya dates badlein."
          );
          toast.error("Room is already booked for selected dates");
        } else {
          setBookingErrorMessage(error.message || "Failed to create booking");
          toast.error(error.message || "Booking failed");
        }
        return;
      }

      // 4. Update room_bookings row with retail_customer_id, purpose_of_stay, and check-in status
      let createdBookingId = data?.booking_id || data?.id;
      if (!createdBookingId) {
        const { data: recentB } = await supabase
          .from("room_bookings")
          .select("id")
          .eq("room_id", selectedRoomId)
          .eq("check_in_date", checkInDate)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        createdBookingId = recentB?.id;
      }

      if (createdBookingId) {
        const bookingPatch: any = {
          retail_customer_id: finalCustomerId,
          purpose_of_stay: purposeOfStay || null,
          guest_name: guestName.trim(),
          guest_phone: guestPhone.trim(),
        };
        if (bookingStatus === "checked_in") {
          bookingPatch.status = "checked_in";
          bookingPatch.actual_check_in_at = new Date().toISOString();
        }
        await supabase.from("room_bookings").update(bookingPatch).eq("id", createdBookingId);
      }

      // Booking successful!
      const nightsCount = data?.nights || pricingSummary.nights;
      const totalCost = (data?.total || pricingSummary.totalAmount).toLocaleString(
        "en-IN"
      );

      toast.success(
        bookingStatus === "checked_in"
          ? `Guest checked in! Room ${roomNumber} — ${nightsCount} nights, Total ₹${totalCost}.`
          : `Booking confirmed! Room ${roomNumber} — ${nightsCount} nights, Total ₹${totalCost}.`,
        { duration: 5000 }
      );

      // Close modal
      setShowBookingModal(false);

      // Refresh Room Grid immediately
      await fetchHotelData(true);
    } catch (err: any) {
      console.error("Booking submission exception:", err);
      setBookingErrorMessage(err?.message || "An unexpected error occurred.");
      toast.error("Failed to complete booking");
    } finally {
      setSubmittingBooking(false);
    }
  };

  return (
    <div
      id="hotel-room-grid-container"
      className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 sm:p-6 space-y-6"
    >
      {/* 1. Header with Title, Refresh & New Booking Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800/80 pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 flex items-center justify-center text-xl shadow-inner">
              🏨
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <span>Live Room Grid & Bookings</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 font-semibold">
                  Hotel View
                </span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                Real-time room availability, live occupancy, housekeeping status, and new guest bookings
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            id="btn-refresh-room-grid"
            title="Refresh Room Grid"
            onClick={() => fetchHotelData(true)}
            className="p-2.5 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-xl transition-colors border border-slate-200 dark:border-slate-800"
          >
            <RefreshCw
              size={17}
              className={refreshing || loading ? "animate-spin text-purple-600" : ""}
            />
          </button>

          <button
            type="button"
            id="btn-new-room-booking"
            onClick={handleOpenNewBookingModal}
            className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-400 text-white active:scale-95 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 shadow-sm transition-all cursor-pointer"
          >
            <Plus size={16} />
            <span>+ New Booking</span>
          </button>

          {onClose && (
            <button
              type="button"
              id="btn-close-room-grid"
              onClick={onClose}
              className="px-3.5 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors border border-slate-200 dark:border-slate-800"
            >
              Wapas Overview
            </button>
          )}
        </div>
      </div>

      {/* 2. Room Status Counters & Legend */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        <button
          type="button"
          onClick={() => setStatusFilter("all")}
          className={`p-3 rounded-xl border text-left transition-all ${
            statusFilter === "all"
              ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 border-slate-900 dark:border-white shadow-sm ring-2 ring-purple-500/20"
              : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60"
          }`}
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider opacity-75">
            Total Rooms
          </p>
          <p className="text-xl font-extrabold mt-0.5">{stats.total}</p>
        </button>

        <button
          type="button"
          id="filter-available"
          onClick={() => setStatusFilter("available")}
          className={`p-3 rounded-xl border text-left transition-all ${
            statusFilter === "available"
              ? "bg-emerald-600 text-white border-emerald-600 shadow-sm ring-2 ring-emerald-500/30"
              : "bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100/70"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider">
              Available
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
          </div>
          <p className="text-xl font-extrabold mt-0.5">{stats.available}</p>
        </button>

        <button
          type="button"
          id="filter-occupied"
          onClick={() => setStatusFilter("occupied")}
          className={`p-3 rounded-xl border text-left transition-all ${
            statusFilter === "occupied"
              ? "bg-red-600 text-white border-red-600 shadow-sm ring-2 ring-red-500/30"
              : "bg-red-50/70 dark:bg-red-950/30 border-red-200 dark:border-red-800/60 text-red-800 dark:text-red-300 hover:bg-red-100/70"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider">
              Occupied
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
          </div>
          <p className="text-xl font-extrabold mt-0.5">{stats.occupied}</p>
        </button>

        <button
          type="button"
          id="filter-dirty"
          onClick={() => setStatusFilter("dirty")}
          className={`p-3 rounded-xl border text-left transition-all ${
            statusFilter === "dirty"
              ? "bg-amber-600 text-white border-amber-600 shadow-sm ring-2 ring-amber-500/30"
              : "bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 hover:bg-amber-100/70"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider">
              Cleaning
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
          </div>
          <p className="text-xl font-extrabold mt-0.5">{stats.dirty}</p>
        </button>

        <button
          type="button"
          id="filter-out-of-order"
          onClick={() => setStatusFilter("out_of_order")}
          className={`p-3 rounded-xl border text-left transition-all ${
            statusFilter === "out_of_order"
              ? "bg-slate-700 text-white border-slate-700 shadow-sm ring-2 ring-slate-500/30"
              : "bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200/60"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider">
              Out of Order
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-slate-400"></span>
          </div>
          <p className="text-xl font-extrabold mt-0.5">{stats.outOfOrder}</p>
        </button>

        <button
          type="button"
          id="filter-reserved"
          onClick={() => setStatusFilter("reserved")}
          className={`p-3 rounded-xl border text-left transition-all ${
            statusFilter === "reserved"
              ? "bg-blue-600 text-white border-blue-600 shadow-sm ring-2 ring-blue-500/30"
              : "bg-blue-50/70 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800/60 text-blue-800 dark:text-blue-300 hover:bg-blue-100/70"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider">
              Reserved
            </span>
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
          </div>
          <p className="text-xl font-extrabold mt-0.5">{stats.reserved}</p>
        </button>
      </div>

      {/* 3. Search and Floor Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-slate-200/80 dark:border-slate-800">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            id="input-search-rooms"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by Room # (e.g. 103), Type (Deluxe), or Guest Name..."
            className="w-full pl-9 pr-4 py-2 text-xs sm:text-sm rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {uniqueFloors.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
            <span className="text-xs font-bold text-slate-400 whitespace-nowrap pl-1">
              Floor:
            </span>
            <button
              type="button"
              onClick={() => setFloorFilter("all")}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                floorFilter === "all"
                  ? "bg-purple-600 text-white"
                  : "bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400"
              }`}
            >
              All Floors
            </button>
            {uniqueFloors.map((fl) => (
              <button
                key={fl}
                type="button"
                onClick={() => setFloorFilter(fl)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                  floorFilter === fl
                    ? "bg-purple-600 text-white"
                    : "bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400"
                }`}
              >
                Floor {fl}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 4. Live Room Cards Grid */}
      {loading ? (
        <div className="py-16 text-center space-y-3">
          <RefreshCw size={28} className="animate-spin text-purple-600 mx-auto" />
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
            Loading Live Room Grid...
          </p>
        </div>
      ) : filteredRooms.length === 0 ? (
        <div className="py-16 text-center bg-slate-50 dark:bg-slate-900/50 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800 p-8 space-y-3">
          <DoorClosed size={36} className="text-slate-400 mx-auto" />
          <h3 className="text-base font-bold text-slate-700 dark:text-slate-300">
            No rooms found
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            {searchQuery || statusFilter !== "all" || floorFilter !== "all"
              ? "Try resetting your search or status filter to see all rooms."
              : "No rooms are registered for this branch yet. Add rooms in Front Desk settings."}
          </p>
          {(searchQuery || statusFilter !== "all" || floorFilter !== "all") && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("all");
                setFloorFilter("all");
              }}
              className="px-3.5 py-1.5 text-xs font-bold bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 rounded-lg"
            >
              Reset Filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3.5 sm:gap-4">
          {filteredRooms.map((room) => {
            const computed = getRoomComputedStatus(room);
            const isBookable =
              computed.status === "available" || computed.status === "reserved";
            const roomTypeName = room.room_types?.type_name || "Standard";
            const basePrice = room.room_types?.base_price || 0;
            const maxGuests = room.room_types?.max_occupancy || 2;
            const floorLabel =
              room.floor !== null && room.floor !== undefined
                ? `Floor ${room.floor}`
                : "Floor 1";

            return (
              <div
                key={room.id}
                id={`room-card-${room.room_number}`}
                className={`relative overflow-hidden rounded-2xl border-2 p-4 transition-all duration-200 flex flex-col justify-between ${computed.cardBg} ${computed.borderClass} ${
                  isBookable ? "hover:shadow-md hover:-translate-y-0.5" : ""
                }`}
              >
                {/* Top: Room Number & Status Badge */}
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-1.5 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider shrink-0">
                          Room
                        </span>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-slate-100 leading-none truncate">
                          {room.room_number}
                        </h3>
                      </div>
                      <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 block truncate">
                        {floorLabel}
                      </span>
                    </div>

                    {/* Status badge */}
                    <div
                      className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider ${computed.badgeText} bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-2xs max-w-[110px] truncate shrink-0`}
                      title={computed.label}
                    >
                      <span
                        className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${computed.badgeBg} shrink-0`}
                      ></span>
                      <span className="truncate">{computed.label}</span>
                    </div>
                  </div>

                  {/* Room Type and Details */}
                  <div className="space-y-1 mt-3 pt-2.5 border-t border-slate-200/50 dark:border-slate-800/60 text-xs">
                    <div className="flex items-center justify-between text-slate-700 dark:text-slate-300">
                      <span className="font-semibold text-purple-700 dark:text-purple-400">
                        {roomTypeName}
                      </span>
                      <span className="font-bold text-slate-900 dark:text-slate-100 flex items-center">
                        ₹{basePrice.toLocaleString("en-IN")}
                        <span className="text-[10px] text-slate-400 font-normal">
                          /nt
                        </span>
                      </span>
                    </div>

                    <div className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                      <Users size={12} className="text-slate-400" />
                      <span>Max {maxGuests} Guests</span>
                    </div>
                  </div>

                  {/* Occupied Details (Guest Name, Checkout) */}
                  {computed.status === "occupied" && computed.booking && (
                    <div className="mt-3 p-2.5 rounded-xl bg-red-100/70 dark:bg-red-950/60 border border-red-200 dark:border-red-900 text-xs space-y-1">
                      <div className="flex items-center gap-1.5 font-bold text-red-900 dark:text-red-200 truncate">
                        <User size={13} className="text-red-600 shrink-0" />
                        <span className="truncate">
                          {computed.booking.guest_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-red-700 dark:text-red-300">
                        <Calendar size={12} className="text-red-500 shrink-0" />
                        <span>Out: {computed.booking.check_out_date}</span>
                      </div>
                    </div>
                  )}

                  {/* Reserved Details */}
                  {computed.status === "reserved" && computed.booking && (
                    <div className="mt-3 p-2.5 rounded-xl bg-blue-100/70 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-900 text-xs space-y-1">
                      <div className="flex items-center gap-1.5 font-bold text-blue-900 dark:text-blue-200 truncate">
                        <CalendarCheck
                          size={13}
                          className="text-blue-600 shrink-0"
                        />
                        <span className="truncate">
                          {computed.booking.guest_name}
                        </span>
                      </div>
                      <div className="text-[11px] text-blue-700 dark:text-blue-300">
                        Check-in: {computed.booking.check_in_date}
                      </div>
                    </div>
                  )}

                  {/* Needs Cleaning details */}
                  {computed.status === "dirty" && (
                    <div className="mt-3 p-2 rounded-xl bg-amber-100/70 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-900 text-[11px] text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                      <AlertTriangle size={13} className="text-amber-600 shrink-0" />
                      <span>Housekeeping pending</span>
                    </div>
                  )}

                  {/* Out of Order details */}
                  {computed.status === "out_of_order" && (
                    <div className="mt-3 p-2 rounded-xl bg-slate-200/70 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-[11px] text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                      <AlertCircle size={13} className="text-slate-500 shrink-0" />
                      <span>Maintenance / Blocked</span>
                    </div>
                  )}
                </div>

                {/* Bottom Action Button */}
                <div className="mt-4 pt-2">
                  {computed.status === "available" && (
                    <button
                      type="button"
                      id={`btn-book-room-${room.room_number}`}
                      onClick={() => handleOpenBookingModalForRoom(room)}
                      className="w-full py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:scale-98 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm transition-all cursor-pointer"
                    >
                      <Plus size={14} />
                      <span>Book Room</span>
                    </button>
                  )}

                  {computed.status === "reserved" && (
                    <button
                      type="button"
                      id={`btn-book-reserved-${room.room_number}`}
                      onClick={() => handleOpenBookingModalForRoom(room)}
                      className="w-full py-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-98 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm transition-all cursor-pointer"
                    >
                      <Plus size={14} />
                      <span>Book New Dates</span>
                    </button>
                  )}

                  {computed.status === "occupied" && (
                    <div className="w-full py-1.5 rounded-xl bg-red-100 dark:bg-red-950/80 text-red-800 dark:text-red-300 font-bold text-[11px] text-center">
                      Currently Occupied
                    </div>
                  )}

                  {computed.status === "dirty" && (
                    <div className="w-full py-1.5 rounded-xl bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 font-bold text-[11px] text-center">
                      Cleaning Required
                    </div>
                  )}

                  {computed.status === "out_of_order" && (
                    <div className="w-full py-1.5 rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-bold text-[11px] text-center">
                      Not Bookable
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. NEW BOOKING MODAL */}
      {showBookingModal && (
        <div
          id="modal-new-room-booking"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto animate-fade-in"
        >
          <div className="bg-white dark:bg-slate-950 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-3xl my-6 max-h-[92vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center text-xl shadow-inner">
                  🛏️
                </div>
                <div>
                  <h3 className="text-lg font-bold">
                    {bookingStatus === "checked_in" ? "Walk-in Guest Check-In" : "New Room Booking"}
                  </h3>
                  <p className="text-xs text-purple-100">
                    Front desk guest verification, KYC documentation & room allocation
                  </p>
                </div>
              </div>
              <button
                type="button"
                id="btn-close-booking-modal"
                onClick={() => setShowBookingModal(false)}
                className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmitBooking} className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {/* Error Notice */}
              {bookingErrorMessage && (
                <div
                  id="booking-error-alert"
                  className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-800 dark:text-rose-200 text-xs flex items-start gap-2.5"
                >
                  <AlertCircle size={17} className="text-rose-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-bold">Booking Warning</p>
                    <p className="mt-0.5 leading-relaxed">{bookingErrorMessage}</p>
                  </div>
                </div>
              )}

              {/* Booking Mode Selector: Advance/Confirmed vs Walk-in Immediate Check-in */}
              <div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  id="btn-mode-advance-booking"
                  onClick={() => setBookingStatus("booked")}
                  className={`py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                    bookingStatus === "booked"
                      ? "bg-white dark:bg-slate-950 text-purple-700 dark:text-purple-300 shadow-sm border border-slate-200/80 dark:border-slate-800"
                      : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                  }`}
                >
                  <Calendar size={14} />
                  <span>Advance Booking (Tentative)</span>
                </button>
                <button
                  type="button"
                  id="btn-mode-walkin-checkin"
                  onClick={() => setBookingStatus("checked_in")}
                  className={`py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                    bookingStatus === "checked_in"
                      ? "bg-purple-600 text-white shadow-sm"
                      : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                  }`}
                >
                  <ShieldCheck size={14} />
                  <span>Walk-in Check-in (Immediate)</span>
                </button>
              </div>

              {/* Room Type & Room Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Room Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="select-room-type"
                    value={selectedRoomTypeId}
                    onChange={(e) => handleRoomTypeChange(e.target.value)}
                    className="w-full h-11 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3.5 text-sm font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                    required
                  >
                    {roomTypes.length === 0 ? (
                      <option value="">No Room Types available</option>
                    ) : (
                      roomTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.type_name} — ₹{t.base_price?.toLocaleString("en-IN")}/nt (Max {t.max_occupancy} guests)
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                      Select Room <span className="text-red-500">*</span>
                    </label>
                    <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                      {availableRoomsForSelectedType.length} available
                    </span>
                  </div>
                  <select
                    id="select-room"
                    value={selectedRoomId}
                    onChange={(e) => {
                      setSelectedRoomId(e.target.value);
                      setBookingErrorMessage(null);
                    }}
                    className="w-full h-11 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3.5 text-sm font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                    required
                  >
                    {availableRoomsForSelectedType.length === 0 ? (
                      <option value="">(No available green rooms for this type)</option>
                    ) : (
                      availableRoomsForSelectedType.map((r) => (
                        <option key={r.id} value={r.id}>
                          Room {r.room_number} — Floor {r.floor || 1} (₹
                          {(r.room_types?.base_price || pricingSummary.basePrice).toLocaleString("en-IN")}
                          /nt)
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              {/* Guest KYC & Verification Section */}
              <GuestKYCSection
                tenantId={currentTenantId || ""}
                phone={guestPhone}
                onPhoneChange={(val) => {
                  setGuestPhone(val);
                  setBookingErrorMessage(null);
                }}
                guestName={guestName}
                onGuestNameChange={(val) => {
                  setGuestName(val);
                  setBookingErrorMessage(null);
                }}
                address={guestAddress}
                onAddressChange={setGuestAddress}
                idProofType={idProofType}
                onIdProofTypeChange={setIdProofType}
                idProofNumber={idProofNumber}
                onIdProofNumberChange={setIdProofNumber}
                purposeOfStay={purposeOfStay}
                onPurposeOfStayChange={setPurposeOfStay}
                selectedFile={idDocFile}
                onFileSelect={setIdDocFile}
                existingDocUrl={existingDocUrl}
                onClearExistingDoc={() => setExistingDocUrl(null)}
                matchedCustomer={matchedCustomer}
                onCustomerMatched={setMatchedCustomer}
                isMandatoryForCheckIn={bookingStatus === "checked_in"}
              />

              {/* Check-in & Check-out Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Check-in Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="date"
                      id="input-check-in-date"
                      value={checkInDate}
                      min={getTodayDateStr()}
                      onChange={(e) => {
                        const newIn = e.target.value;
                        setCheckInDate(newIn);
                        setBookingErrorMessage(null);
                        // Ensure checkout is after checkin
                        if (checkOutDate <= newIn) {
                          setCheckOutDate(getTomorrowDateStr(newIn));
                        }
                      }}
                      className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Check-out Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarCheck
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="date"
                      id="input-check-out-date"
                      value={checkOutDate}
                      min={getTomorrowDateStr(checkInDate)}
                      onChange={(e) => {
                        setCheckOutDate(e.target.value);
                        setBookingErrorMessage(null);
                      }}
                      className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* 5. Number of Guests & Payment Method */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Number of Guests <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Users
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="number"
                      id="input-num-guests"
                      value={numGuests}
                      min={1}
                      max={
                        pricingSummary.selectedRoom?.room_types?.max_occupancy ||
                        6
                      }
                      onChange={(e) =>
                        setNumGuests(Math.max(1, parseInt(e.target.value) || 1))
                      }
                      className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CreditCard
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <select
                      id="select-payment-method"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/20 focus:outline-none"
                      required
                    >
                      <option value="pay_later">
                        Pay Later / At Checkout (Default)
                      </option>
                      <option value="cash">Cash Payment</option>
                      <option value="upi">UPI / Online QR</option>
                      <option value="card">Debit / Credit Card</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 6. Pricing Summary Breakdown Box */}
              <div className="p-4 rounded-2xl bg-purple-50/70 dark:bg-purple-950/30 border border-purple-200/80 dark:border-purple-900/60 space-y-2 text-xs">
                <div className="flex items-center justify-between text-purple-900 dark:text-purple-200 font-bold">
                  <span>Reservation Estimate</span>
                  <span>
                    {pricingSummary.nights} Night
                    {pricingSummary.nights > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="space-y-1 pt-1 border-t border-purple-200/60 dark:border-purple-900/60 text-slate-600 dark:text-slate-300">
                  <div className="flex justify-between">
                    <span>
                      Room Charge (₹
                      {pricingSummary.basePrice.toLocaleString("en-IN")} ×{" "}
                      {pricingSummary.nights} nt):
                    </span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      ₹{pricingSummary.roomCharge.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Estimated GST (12%):</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      ₹{pricingSummary.gstAmount.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-purple-200/60 dark:border-purple-900/60 text-sm font-extrabold text-purple-900 dark:text-purple-100">
                    <span>Total Estimated:</span>
                    <span>
                      ₹{pricingSummary.totalAmount.toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  id="btn-confirm-room-booking"
                  disabled={submittingBooking || !selectedRoomId}
                  className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs sm:text-sm font-bold flex items-center gap-2 shadow-sm active:scale-95 transition-all cursor-pointer"
                >
                  {submittingBooking ? (
                    <>
                      <RefreshCw size={15} className="animate-spin" />
                      <span>{bookingStatus === "checked_in" ? "Processing Check-in..." : "Confirming Booking..."}</span>
                    </>
                  ) : (
                    <>
                      {bookingStatus === "checked_in" ? <ShieldCheck size={16} /> : <Check size={16} />}
                      <span>{bookingStatus === "checked_in" ? "Complete Check-in & Allot Room" : "Confirm & Book Room"}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
